import React, { useState, useMemo } from 'react';
import { useCarePlans, useUpdateCarePlan } from '../state/data/carePlan';
import TaskQueue from './TaskQueue';
import ConstrainedChips from './ConstrainedChips';
import { RISK_LEVELS, PLAN_STATUSES, CONDITIONS, DEVICES, LIFESTYLE_FOCUS, CONTENT_TRACKS } from '../data/carePlanOptions';
import { normalizeCondition } from '@/logic/alignmentConditions';

const RISK_COLORS = {
  standard: 'var(--wi-status-good)',
  elevated: '#F59E0B',
  high: 'var(--wi-status-bad)',
  critical: '#7C3AED',
};

/* ── Editable text list ───────────────────────────────────────── */
function EditableList({ items, onChange }) {
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');

  function handleAdd() {
    const val = newText.trim();
    if (val) onChange([...items, val]);
    setNewText('');
    setAdding(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span className="body2" style={{ flex: 1, lineHeight: 1.5 }}>{item}</span>
          <button
            onClick={() => onChange(items.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--wi-status-bad)', padding: '2px 4px', flexShrink: 0, lineHeight: 1 }}
          >×</button>
        </div>
      ))}
      {adding ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            autoFocus
            value={newText}
            onChange={e => setNewText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') { setAdding(false); setNewText(''); } }}
            placeholder="New item…"
            style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--wi-divider)', fontSize: 13, fontFamily: 'var(--wi-font-family)', outline: 'none' }}
          />
          <button className="btn-secondary" style={{ fontSize: 12 }} onClick={handleAdd}>Add</button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{ alignSelf: 'flex-start', background: 'none', border: '1px dashed var(--wi-divider)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, color: 'var(--wi-text-secondary)', fontFamily: 'var(--wi-font-family)' }}
        >+ Add</button>
      )}
    </div>
  );
}

/* ── Care plan editor ─────────────────────────────────────────── */
function CarePlanEditor({ plan, patientName, onSave, onBack }) {
  const [draft, setDraft] = useState({ ...plan });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  function update(field, value) {
    setDraft(prev => ({ ...prev, [field]: value }));
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const updates = {
        conditions: draft.conditions,
        condition_slugs: draft.condition_slugs,
        risk_level: draft.risk_level,
        status: draft.status,
        devices: draft.devices,
        daily_tasks: draft.daily_tasks,
        lifestyle_focus: draft.lifestyle_focus,
        content_curriculum: draft.content_curriculum,
        exercise_plan: draft.exercise_plan,
        nutrition_plan: draft.nutrition_plan,
      };
      await onSave(plan.id, plan.withings_user_id, updates);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const targets = draft.targets || {};
  const exercise = draft.exercise_plan || {};
  const nutrition = draft.nutrition_plan || {};
  const monitoring = draft.monitoring_cadence || {};

  const selectStyle = {
    padding: '5px 8px', borderRadius: 6,
    border: '1px solid var(--wi-divider)',
    background: 'var(--wi-background-secondary)',
    color: 'var(--wi-text-primary)', fontSize: 13,
    fontFamily: 'var(--wi-font-family)', outline: 'none', cursor: 'pointer',
  };

  const inputStyle = {
    padding: '5px 8px', borderRadius: 6,
    border: '1px solid var(--wi-divider)',
    fontSize: 13, fontFamily: 'var(--wi-font-family)', outline: 'none',
    width: 80,
  };

  const sectionStyle = {
    background: 'var(--wi-background-secondary)',
    borderRadius: 10, padding: '16px 20px',
    border: '1px solid var(--wi-divider)',
  };

  return (
    <div style={{ marginBottom: 32 }}>
      {/* Back + header */}
      <button
        onClick={onBack}
        className="body2"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--wi-action-primary)', padding: 0, marginBottom: 16 }}
      >
        ← Back to care plans
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <h3 className="header3" style={{ margin: 0 }}>{patientName || `Patient ${plan.withings_user_id}`}</h3>
        <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 4, background: 'var(--wi-background-info)', color: 'var(--wi-text-primary-info)' }}>
          {draft.track}
        </span>
        <span className="detail1 text-secondary">CKM Stage {draft.ckm_stage}</span>
        <span className="detail1 text-secondary">v{draft.version}</span>
      </div>

      {/* Risk + Status */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        <div>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 6px' }}>Risk level</p>
          <select style={selectStyle} value={draft.risk_level || ''} onChange={e => update('risk_level', e.target.value)}>
            {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 6px' }}>Status</p>
          <select style={selectStyle} value={draft.status || ''} onChange={e => update('status', e.target.value)}>
            {PLAN_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Conditions */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Conditions</p>
          <ConstrainedChips items={draft.conditions || []} options={CONDITIONS} normalizeFn={normalizeCondition} onChange={v => update('conditions', v)} placeholder="Add condition..." labelFn={v => v} />
        </div>

        {/* Clinical targets (read-only) */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Clinical targets</p>
          {Object.keys(targets).length === 0 ? (
            <p className="body2 text-secondary" style={{ margin: 0 }}>No targets defined</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(targets).map(([key, t]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="body2">{key.replace(/_/g, ' ')}</span>
                  <span className="body2 text-secondary">
                    {t.display || `${t.operator} ${t.value}`}
                    {t.guideline && <span className="detail1" style={{ marginLeft: 8, color: 'var(--wi-status-info)' }}>{t.guideline}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Devices */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Devices</p>
          <ConstrainedChips items={draft.devices || []} options={DEVICES} onChange={v => update('devices', v)} placeholder="Add device..." labelFn={v => v} />
        </div>

        {/* Daily tasks */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Daily tasks</p>
          <EditableList items={draft.daily_tasks || []} onChange={v => update('daily_tasks', v)} />
        </div>

        {/* Monitoring (read-only) */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Monitoring cadence</p>
          {Object.keys(monitoring).length === 0 ? (
            <p className="body2 text-secondary" style={{ margin: 0 }}>Not configured</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(monitoring).map(([key, m]) => (
                <span key={key} className="body2">
                  {key.toUpperCase()}: {m.times_per_week}x/week
                  {m.time_of_day?.length > 0 && `, ${m.time_of_day.join(' + ')}`}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Lifestyle */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Lifestyle focus</p>
          <ConstrainedChips items={draft.lifestyle_focus || []} options={LIFESTYLE_FOCUS} onChange={v => update('lifestyle_focus', v)} placeholder="Add focus area..." />
        </div>

        {/* Exercise plan */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Exercise plan</p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="detail1 text-secondary">
              Type
              <input style={{ ...inputStyle, width: 120, marginLeft: 6 }} value={exercise.type || ''} onChange={e => update('exercise_plan', { ...exercise, type: e.target.value })} />
            </label>
            <label className="detail1 text-secondary">
              Intensity
              <select style={{ ...selectStyle, marginLeft: 6 }} value={exercise.intensity || ''} onChange={e => update('exercise_plan', { ...exercise, intensity: e.target.value })}>
                <option value="light">Light</option>
                <option value="moderate">Moderate</option>
                <option value="vigorous">Vigorous</option>
              </select>
            </label>
            <label className="detail1 text-secondary">
              Days/wk
              <input type="number" min={1} max={7} style={{ ...inputStyle, width: 50, marginLeft: 6 }} value={exercise.days_per_week || ''} onChange={e => update('exercise_plan', { ...exercise, days_per_week: Number(e.target.value) })} />
            </label>
            <label className="detail1 text-secondary">
              Min/day
              <input type="number" min={5} max={120} style={{ ...inputStyle, width: 50, marginLeft: 6 }} value={exercise.minutes_per_day || ''} onChange={e => update('exercise_plan', { ...exercise, minutes_per_day: Number(e.target.value) })} />
            </label>
          </div>
          {exercise.restrictions && (
            <p className="detail1" style={{ margin: '8px 0 0', color: 'var(--wi-status-bad)' }}>
              Restrictions: {exercise.restrictions}
            </p>
          )}
        </div>

        {/* Nutrition plan */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Nutrition plan</p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="detail1 text-secondary">
              Pattern
              <input style={{ ...inputStyle, width: 100, marginLeft: 6 }} value={nutrition.pattern || ''} onChange={e => update('nutrition_plan', { ...nutrition, pattern: e.target.value })} />
            </label>
            <label className="detail1 text-secondary">
              Sodium limit
              <input type="number" style={{ ...inputStyle, width: 70, marginLeft: 6 }} value={nutrition.sodium_limit_mg || ''} onChange={e => update('nutrition_plan', { ...nutrition, sodium_limit_mg: Number(e.target.value) })} />
              <span style={{ marginLeft: 4 }}>mg</span>
            </label>
          </div>
        </div>

        {/* Content curriculum */}
        <div style={sectionStyle}>
          <p className="eyebrow text-secondary" style={{ margin: '0 0 10px' }}>Content curriculum</p>
          <ConstrainedChips items={draft.content_curriculum || []} options={CONTENT_TRACKS} onChange={v => update('content_curriculum', v)} placeholder="Add track..." />
        </div>
      </div>

      {/* Save bar */}
      <div style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : saved ? 'Saved ✓' : 'Save changes'}
        </button>
        {saveError && <span className="detail1" style={{ color: 'var(--wi-status-bad)' }}>{saveError}</span>}
      </div>
    </div>
  );
}

/* ── Care plan row ────────────────────────────────────────────── */
function CarePlanRow({ plan, patientName, onClick }) {
  const riskColor = RISK_COLORS[plan.risk_level] || 'var(--wi-text-secondary)';
  return (
    <div
      onClick={onClick}
      className="table-cell-component"
      style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 2fr 1fr 0.7fr', gap: 8, alignItems: 'center', cursor: 'pointer', padding: '12px 20px' }}
    >
      <span>
        <span className="header4" style={{ textTransform: 'capitalize', display: 'block' }}>
          {patientName || `ID ${plan.withings_user_id}`}
        </span>
        <span className="detail1 text-secondary">{plan.withings_user_id}</span>
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: 'var(--wi-background-info)', color: 'var(--wi-text-primary-info)', textAlign: 'center' }}>
        {plan.track}
      </span>
      <span className="body2" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(plan.conditions || []).map((c, i) => (
          <span key={i} style={{ fontSize: 12, padding: '1px 7px', borderRadius: 4, background: 'var(--wi-background-tertiary)' }}>{c}</span>
        ))}
      </span>
      <span style={{ fontSize: 12, fontWeight: 600, color: riskColor }}>{plan.risk_level}</span>
      <span className="detail1 text-secondary">{plan.status}</span>
    </div>
  );
}

/* ── Main ClinicalLeadTab ─────────────────────────────────────── */
export default function ClinicalLeadTab({ allPatients = [] }) {
  const plansQuery = useCarePlans();
  const carePlans = plansQuery.data ?? [];
  const plansLoading = plansQuery.isLoading;
  const plansError = plansQuery.error?.message ?? null;
  const updatePlanMut = useUpdateCarePlan();
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const patientNames = useMemo(() => {
    const map = new Map();
    allPatients.forEach(p => {
      const name = [p.firstname, p.lastname].filter(Boolean).join(' ');
      if (name) map.set(String(p.userid), name);
    });
    return map;
  }, [allPatients]);

  async function handleSavePlan(id, withingsUserId, updates) {
    await updatePlanMut.mutateAsync({ withingsUserId, updates });
    // TanStack invalidates CARE_PLAN_KEYS.list() in onSuccess → query refetches.
  }

  const selectedPlan = carePlans.find(p => p.id === selectedPlanId);

  // Detail / edit view
  if (selectedPlan) {
    return (
      <CarePlanEditor
        plan={selectedPlan}
        patientName={patientNames.get(String(selectedPlan.withings_user_id))}
        onSave={handleSavePlan}
        onBack={() => setSelectedPlanId(null)}
      />
    );
  }

  // List view
  return (
    <div style={{ marginBottom: 32 }}>
      {/* Tasks assigned to RN (clinical lead tasks) */}
      <TaskQueue allPatients={allPatients} roleFilter="rn" />

      {/* Care plans section */}
      <div className="section-title-component" style={{ marginTop: 8 }}>
        <span className="header3">Care Plans</span>
      </div>

      {plansLoading && <p className="body2 text-secondary" style={{ padding: '24px 0' }}>Loading care plans…</p>}
      {plansError && <p className="body2" style={{ color: 'var(--wi-status-bad)', padding: '24px 0' }}>Failed to load care plans: {plansError}</p>}

      {!plansLoading && !plansError && (
        <>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 2fr 1fr 0.7fr', gap: 8, padding: '8px 20px' }}>
            {['Patient', 'Track', 'Conditions', 'Risk', 'Status'].map(h => (
              <span key={h} className="eyebrow text-secondary">{h}</span>
            ))}
          </div>

          {carePlans.length === 0 ? (
            <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, padding: '32px 20px', textAlign: 'center' }}>
              <p className="body2 text-secondary" style={{ margin: 0 }}>No care plans found.</p>
            </div>
          ) : (
            <div className="table-component">
              {carePlans.map(plan => (
                <CarePlanRow
                  key={plan.id}
                  plan={plan}
                  patientName={patientNames.get(String(plan.withings_user_id))}
                  onClick={() => setSelectedPlanId(plan.id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
